import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const reasons = [
  {
    icon: 'ri-medal-2-line',
    title: 'Certified & Credentialed Experts',
    description: 'Our team holds over 5+ active industry certifications across Microsoft, AWS, Google Cloud, Cisco, CompTIA, and leading cybersecurity frameworks like CISSP, CEH and CISM.',
    stats: '5+ certifications across &nbsp;6 specialists',
    color: 'orange',
  },
  {
    icon: 'ri-radar-line',
    title: 'Proactive, Not Reactive Monitoring',
    description: 'Our AI-powered monitoring platform analyses millions of events per day to identify and resolve anomalies before they become business-impacting incidents.',
    stats: '99.9% uptime achieved for managed clients',
    color: 'amber',
  },
  {
    icon: 'ri-expand-width-line',
    title: 'Solutions That Scale With You',
    description: 'We design every solution with growth in mind. Whether you\'re a 20-person team or a 5,000-person enterprise, our architecture grows alongside your ambitions.',
    stats: 'Clients range from 5 to 5,000 employees',
    color: 'orange',
  },
  {
    icon: 'ri-speed-line',
    title: 'Industry-Leading Response Times',
    description: 'Critical issues receive a response in under 15 minutes, 24 hours a day, 7 days a week. We back this with contractual SLAs and transparent reporting.',
    stats: 'Under 15 min avg. response for P1 issues',
    color: 'amber',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Security-First at Every Layer',
    description: 'Security isn\'t a feature — it\'s our foundation. Every solution we design incorporates defense-in-depth principles, minimizing your attack surface at every level.',
    stats: 'Zero client data breaches since founding',
    color: 'orange',
  },
  {
    icon: 'ri-user-heart-line',
    title: 'Dedicated Client Success Managers',
    description: 'Every client gets a named Client Success Manager who knows your business, advocates for your needs internally, and ensures you\'re always getting maximum value.',
    stats: '97% client satisfaction score (2024)',
    color: 'amber',
  },
  {
    icon: 'ri-file-check-line',
    title: 'Transparent Pricing & Contracts',
    description: 'No hidden fees, no surprise invoices. Our pricing is straightforward, our contracts are fair, and we provide monthly reports so you always know exactly what you\'re getting.',
    stats: 'Month-to-month options available',
    color: 'orange',
  },
  {
    icon: 'ri-global-line',
    title: 'Multi-Industry Experience',
    description: 'We\'ve served clients across healthcare (HIPAA), finance (SOX, PCI DSS), manufacturing, retail, and legal sectors — bringing industry-specific compliance expertise to every engagement.',
    stats: '12+ regulated industries served',
    color: 'amber',
  },
];

const colorMap: Record<string, { icon: string; stat: string; border: string }> = {
  orange: { icon: 'text-orange-600 bg-orange-50', stat: 'bg-orange-50 text-orange-700', border: 'border-orange-200' },
  amber: { icon: 'text-amber-600 bg-amber-50', stat: 'bg-amber-50 text-amber-700', border: 'border-amber-200' },
};

export default function WhyChooseUsPage() {
  return (
    <div className="min-h-screen bg-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-10 bg-white">
        <div className="mx-auto px-6 lg:px-10 xl:px-16 text-center">
          <span className="eyebrow">Our Difference</span>
          <h1 className="text-3xl lg:text-4xl font-extrabold text-page-ink mb-3 tracking-[-0.03em]">Why Choose SunStone Systems?</h1>
          <p className="text-page-muted text-base max-w-3xl mx-auto leading-relaxed font-light">
            In a crowded Nigerian IT market, here's what makes us demonstrably, measurably different — backed by data, not marketing copy.
          </p>
        </div>
      </section>

      {/* Quick Metrics */}
      <section className="py-10 bg-white border-y border-black/[0.06]">
        <div className="mx-auto px-6 lg:px-10 xl:px-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { value: '20+', label: 'Businesses Served' },
              { value: '99.9%', label: 'Uptime SLA Achieved' },
              { value: '97%', label: 'Client Satisfaction' },
              { value: '15 min', label: 'Avg. Response (P1)' },
            ].map(({ value, label }) => (
              <div key={label}>
                <div className="text-3xl lg:text-4xl font-bold text-[#c97a2b] font-display mb-1">{value}</div>
                <div className="text-page-muted text-sm">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reasons Grid */}
      <section className="py-10 mx-auto px-6 lg:px-10 xl:px-16">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <span className="eyebrow">8 Reasons to Partner With Us</span>
          <h2 className="text-3xl font-extrabold text-page-ink mb-3 tracking-[-0.02em]">Built Different. Proven Results.</h2>
          <p className="text-page-muted text-base font-light">Every point below is backed by data, client testimonials, and industry recognition.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {reasons.map((reason, i) => {
            const colors = colorMap[reason.color];
            return (
              <div
                key={reason.title}
                className="group flex space-x-5 p-7 rounded-xl border border-black/[0.08] bg-white hover:border-[#c97a2b]/35 transition-all duration-300 shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
              >
                <div className="flex-shrink-0">
                  <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${colors.icon}`}>
                    <i className={`${reason.icon} text-xl`} />
                  </div>
                </div>
                <div>
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-bold text-page-ink text-base leading-tight">{reason.title}</h3>
                    <span className="text-page-faint text-xs font-medium ml-2 whitespace-nowrap">0{i + 1}</span>
                  </div>
                  <p className="text-page-muted text-sm leading-relaxed mb-4">{reason.description}</p>
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-[#c97a2b]/10 border border-[#c97a2b]/25 text-[#c97a2b]">
                    {reason.stats}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-white">
        <div className="mx-auto px-6 lg:px-10 xl:px-16 text-center max-w-2xl">
          <h2 className="text-3xl font-extrabold text-page-ink mb-4 tracking-[-0.02em]">Ready to Experience the Difference?</h2>
          <p className="text-page-muted text-base mb-6 font-light">
            Join 500+ businesses who chose SunStone Systems. Start with a free, no-obligation assessment.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/contact" className="btn-primary">
              Book Free Consultation
              <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/services" className="btn-ghost">
              View Our Services
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
